import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import './index.css';
import TimePeriodSelector from './TimePeriodSelector';
import useUserStore from './UserStore';

import { 
  ArrowDownIcon, 
  ArrowUpIcon, 
  PhoneIcon,
  MicrophoneIcon,
  EyeIcon,
  PlusIcon,
  CalendarIcon,
  ChevronDownIcon
} from '@heroicons/react/20/solid'
import Titlebar from './Titlebar';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from './config';

// Mock data - replace with API calls
const mockRecentOrders = [
  { id: 'ORD-001', customer: 'ABC Corporation', amount: '$1,250.00', status: 'Completed', date: '2025-11-17', type: 'Cash' },
  { id: 'ORD-002', customer: 'XYZ Industries', amount: '$875.00', status: 'Processing', date: '2025-11-16', type: 'Trade' },
  { id: 'ORD-003', customer: 'Local Business', amount: '$2,100.00', status: 'Completed', date: '2025-11-15', type: 'Cash' },
  { id: 'ORD-004', customer: 'Radio Station WXYZ', amount: '$650.00', status: 'Pending', date: '2025-11-14', type: 'Trade' },
  { id: 'ORD-005', customer: 'Marketing Agency', amount: '$1,800.00', status: 'Completed', date: '2025-11-13', type: 'Cash' },
];

const mockTopSpots = [
  { rank: 1, cart: '90246-001', title: 'May the 4th Sale', customer: 'Mr. Nice Guys', status: 'live', priority: 'high' },
  { rank: 2, cart: '90247-002', title: 'Summer Clearance', customer: 'Auto Dealership', status: 'approved', priority: 'medium' },
  { rank: 3, cart: '90248-003', title: 'Back to School', customer: 'Local Store', status: 'produced', priority: 'low' },
];

// Recent Orders Table Component
function RecentOrdersTable({ orders }) {
  const navigate = useNavigate();
  const formatDate = (value) => {
    if (!value) {
      return '';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }

    return parsedDate.toISOString().split('T')[0];
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Recent Orders</h3>
          <button
            onClick={() => navigate('/orders')}
            className="text-sm text-indigo-600 hover:text-indigo-500 flex items-center"
          >
            <EyeIcon className="h-4 w-4 mr-1" />
            View All
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-3 py-4 text-sm text-gray-900">{order.customer}</td>
                  <td className="px-3 py-4 text-sm text-gray-900">{order.amount}</td>
                  <td className="px-3 py-4 text-sm">
                    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                      order.type === 'Cash' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-blue-50 text-blue-700 ring-blue-600/20'
                    }`}>
                      {order.type}
                    </span>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">{formatDate(order.startDate)}</td>
                  <td className="px-3 py-4 text-sm text-gray-500">{formatDate(order.endDate)}</td>
                  <td className="px-3 py-4 text-sm text-indigo-600">
                    <button
                      type="button"
                      className="hover:text-indigo-500"
                      onClick={() => navigate(`/order/view/${order.id}`)}
                    >
                      View
                    </button>
                    <span className="mx-2 text-gray-400">|</span>
                    <button
                      type="button"
                      className="hover:text-indigo-500"
                      onClick={() => navigate(`/orderentry/edit/${order.id}`)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Sales Call Tracker Component
function SalesCallTracker() {
  const [calls, setCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingCallId, setEditingCallId] = useState(null);
  const [closeOutCallId, setCloseOutCallId] = useState(null);
  const [closeOutReason, setCloseOutReason] = useState('');
  const user = useUserStore((state) => state.user);
  const salesRepEmail = user?.email
    || user?.user?.email
    || user?.authResponse?.profile?.email
    || '';
  const [newCall, setNewCall] = useState({
    client_name: '',
    status: 'pending',
    call_notes: '',
    email: '',
    phone: '',
    email_type: 'work',
    phone_type: 'cell',
    sales_rep_email: salesRepEmail,
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const navigate = useNavigate();

  const fetchCallsForSalesRep = useCallback(async () => {
    if (!salesRepEmail) {
      setCalls([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const encodedEmail = encodeURIComponent(salesRepEmail);
      const response = await fetch(apiUrl(`/sales/calls/${encodedEmail}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load calls: ${response.statusText}`);
      }

      const result = await response.json();
      const fetchedCalls = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
          ? result.data
          : [];

      setCalls(fetchedCalls);
    } catch (err) {
      console.error('Error loading calls:', err);
      setError(err.message || 'Failed to load calls');
    } finally {
      setIsLoading(false);
    }
  }, [salesRepEmail]);

  useEffect(() => {
    setNewCall((prev) => ({
      ...prev,
      sales_rep_email: salesRepEmail,
    }));
  }, [salesRepEmail]);

  useEffect(() => {
    fetchCallsForSalesRep();
  }, [fetchCallsForSalesRep]);

  const addCall = async () => {
    if (!newCall.client_name.trim()) {
      setError('Client name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        client_name: newCall.client_name,
        status: newCall.status,
        call_notes: newCall.call_notes,
        email: newCall.email,
        phone: newCall.phone,
        email_type: newCall.email_type,
        phone_type: newCall.phone_type,
        sales_rep_email: salesRepEmail,
      };

      const response = await fetch(apiUrl(`/sales/calls`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to add call: ${response.statusText}`);
      }

      const savedCall = await response.json();
      setCalls([savedCall, ...calls]);
      setNewCall({
        client_name: '',
        status: 'pending',
        call_notes: '',
        email: '',
        phone: '',
        email_type: 'work',
        phone_type: 'cell',
        sales_rep_email: salesRepEmail,
      });
      setShowAddForm(false);
    } catch (err) {
      console.error('Error adding call:', err);
      setError(err.message || 'Failed to add call');
    } finally {
      setIsLoading(false);
    }
  };

  const updateCall = async (callId) => {
    const callToUpdate = calls.find(c => c.id === callId);
    if (!callToUpdate || !callToUpdate.client_name.trim()) {
      setError('Client name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        client_name: callToUpdate.client_name,
        status: callToUpdate.status,
        call_notes: callToUpdate.call_notes,
        email: callToUpdate.email,
        phone: callToUpdate.phone,
        email_type: callToUpdate.email_type,
        phone_type: callToUpdate.phone_type,
        sales_rep_email: callToUpdate.sales_rep_email,
      };

      const response = await fetch(apiUrl(`/sales/calls/${callId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to update call: ${response.statusText}`);
      }

      await fetchCallsForSalesRep();
      setEditingCallId(null);
    } catch (err) {
      console.error('Error updating call:', err);
      setError(err.message || 'Failed to update call');
    } finally {
      setIsLoading(false);
    }
  };

  const convertToOrder = (call) => {
    // Navigate to order entry with pre-filled customer data
    navigate(`/orderentry?customer=${encodeURIComponent(call.client_name)}&notes=${encodeURIComponent(call.call_notes)}`);
  };

  const addCustomerFromCall = (call) => {
    const customerName = call?.client_name ? encodeURIComponent(call.client_name) : '';
    navigate(customerName ? `/customeradd?name=${customerName}` : '/customeradd');
  };

  const followUpCall = (callId) => {
    setEditingCallId(callId);
  };

  const openCloseOutPanel = (callId) => {
    setCloseOutCallId(callId);
    setCloseOutReason('');
    setError(null);
  };

  const cancelCloseOut = () => {
    setCloseOutCallId(null);
    setCloseOutReason('');
  };

  const closeOutCall = async (callId) => {
    const callToUpdate = calls.find(c => c.id === callId);
    if (!callToUpdate) return;

    if (!closeOutReason || !closeOutReason.trim()) {
      setError('A close out reason is required.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const existingNotes = callToUpdate.call_notes ? `${callToUpdate.call_notes}\n` : '';
      const payload = {
        client_name: callToUpdate.client_name,
        status: 'closed-out',
        call_notes: `${existingNotes}Close-out reason: ${closeOutReason.trim()}`,
        email: callToUpdate.email,
        phone: callToUpdate.phone,
        email_type: callToUpdate.email_type,
        phone_type: callToUpdate.phone_type,
        sales_rep_email: callToUpdate.sales_rep_email,
      };

      const response = await fetch(apiUrl(`/sales/calls/${callId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to close out call: ${response.statusText}`);
      }

      const updatedCall = await response.json();
      setCalls(calls.map(c => c.id === callId ? updatedCall : c));
      setCloseOutCallId(null);
      setCloseOutReason('');
    } catch (err) {
      console.error('Error closing out call:', err);
      setError(err.message || 'Failed to close out call');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCallTimestamp = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return `${parsed.toLocaleDateString()} at ${parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const visibleCalls = calls.filter((call) => call.status !== 'closed-out');
  const closedOutCount = calls.length - visibleCalls.length;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center">
            <PhoneIcon className="h-5 w-5 mr-2 text-indigo-500" />
            Sales Call Tracker
          </h3>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            disabled={isLoading}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Call
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {showAddForm && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <input
                type="text"
                placeholder="Client name"
                value={newCall.client_name}
                onChange={(e) => setNewCall({ ...newCall, client_name: e.target.value })}
                disabled={isLoading}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
              />
              <select
                value={newCall.status}
                onChange={(e) => setNewCall({ ...newCall, status: e.target.value })}
                disabled={isLoading}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
              >
                <option value="interested">Interested</option>
                <option value="follow-up">Follow-up Required</option>
                <option value="checking-budget">Checking Budget</option>
                <option value="not-interested">Not Interested</option>
              </select>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-2">
              <input
                type="email"
                placeholder="Email"
                value={newCall.email}
                onChange={(e) => setNewCall({ ...newCall, email: e.target.value })}
                disabled={isLoading}
                className="block col-span-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
              />
              <select
                value={newCall.email_type}
                onChange={(e) => setNewCall({ ...newCall, email_type: e.target.value })}
                disabled={isLoading}
                className="block col-span-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
              >
                <option value="work">Work</option>
                <option value="personal">Personal</option>
              </select>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-2">
              <input
                type="tel"
                placeholder="Phone"
                value={newCall.phone}
                onChange={(e) => setNewCall({ ...newCall, phone: e.target.value })}
                disabled={isLoading}
                className="block col-span-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
              />
              <select
                value={newCall.phone_type}
                onChange={(e) => setNewCall({ ...newCall, phone_type: e.target.value })}
                disabled={isLoading}
                className="block col-span-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
              >
                <option value="cell">Cell</option>
                <option value="home">Home</option>
                <option value="work">Work</option>
              </select>
            </div>
            <textarea
              placeholder="Call notes..."
              value={newCall.call_notes}
              onChange={(e) => setNewCall({ ...newCall, call_notes: e.target.value })}
              disabled={isLoading}
              className="mt-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
              rows={2}
            />
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={() => setShowAddForm(false)}
                disabled={isLoading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={addCall}
                disabled={isLoading}
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400"
              >
                {isLoading ? 'Saving...' : 'Add Call'}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {calls.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No calls tracked today. Add your first call above!</p>
          ) : visibleCalls.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              All tracked calls are closed out.
              {closedOutCount > 0 ? ` ${closedOutCount} closed-out call${closedOutCount === 1 ? '' : 's'} are hidden.` : ''}
            </p>
          ) : (
            visibleCalls
              .slice(0, 5)
              .map((call) => {
              const isEditing = editingCallId === call.id;
              const editingCall = isEditing ? calls.find(c => c.id === call.id) : null;

              if (isEditing && editingCall) {
                return (
                  <div key={call.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mb-3">
                      <input
                        type="text"
                        placeholder="Client name"
                        value={editingCall.client_name}
                        onChange={(e) => setCalls(calls.map(c => c.id === call.id ? { ...c, client_name: e.target.value } : c))}
                        disabled={isLoading}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                      />
                      <select
                        value={editingCall.status}
                        onChange={(e) => setCalls(calls.map(c => c.id === call.id ? { ...c, status: e.target.value } : c))}
                        disabled={isLoading}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                      >
                        <option value="interested">Interested</option>
                        <option value="follow-up">Follow-up Required</option>
                        <option value="checking-budget">Checking Budget</option>
                        <option value="not-interested">Not Interested</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-2 mb-3">
                      <input
                        type="email"
                        placeholder="Email"
                        value={editingCall.email}
                        onChange={(e) => setCalls(calls.map(c => c.id === call.id ? { ...c, email: e.target.value } : c))}
                        disabled={isLoading}
                        className="block col-span-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                      />
                      <select
                        value={editingCall.email_type}
                        onChange={(e) => setCalls(calls.map(c => c.id === call.id ? { ...c, email_type: e.target.value } : c))}
                        disabled={isLoading}
                        className="block col-span-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                      >
                        <option value="work">Work</option>
                        <option value="personal">Personal</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-2 mb-3">
                      <input
                        type="tel"
                        placeholder="Phone"
                        value={editingCall.phone}
                        onChange={(e) => setCalls(calls.map(c => c.id === call.id ? { ...c, phone: e.target.value } : c))}
                        disabled={isLoading}
                        className="block col-span-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                      />
                      <select
                        value={editingCall.phone_type}
                        onChange={(e) => setCalls(calls.map(c => c.id === call.id ? { ...c, phone_type: e.target.value } : c))}
                        disabled={isLoading}
                        className="block col-span-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                      >
                        <option value="cell">Cell</option>
                        <option value="home">Home</option>
                        <option value="work">Work</option>
                      </select>
                    </div>
                    <textarea
                      placeholder="Call notes..."
                      value={editingCall.call_notes}
                      onChange={(e) => setCalls(calls.map(c => c.id === call.id ? { ...c, call_notes: e.target.value } : c))}
                      disabled={isLoading}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm mb-3 disabled:bg-gray-100"
                      rows={2}
                    />
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setEditingCallId(null)}
                        disabled={isLoading}
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:bg-gray-100"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => updateCall(call.id)}
                        disabled={isLoading}
                        className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400"
                      >
                        {isLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={call.id}>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900">{call.client_name}</span>
                        <span className={`ml-2 inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          call.status === 'completed' ? 'bg-green-100 text-green-800' :
                          call.status === 'closed-out' ? 'bg-red-100 text-red-800' :
                          call.status === 'interested' ? 'bg-blue-100 text-blue-800' :
                          call.status === 'follow-up' ? 'bg-yellow-100 text-yellow-800' :
                          call.status === 'not-interested' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {call.status}
                        </span>
                      </div>
                      <p className="text-xs pb-2 text-gray-500">
                        {call.email ? `${call.email} (${call.email_type})` : 'No email'}
                        {' • '}
                        {call.phone ? `${call.phone} (${call.phone_type})` : 'No phone'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">{call.call_notes?.match(/[^.!?]+[.!?]?/)?.[0]?.trim() || ''}</p>
                      <p className="text-xs pt-2 text-gray-500">
                        {formatCallTimestamp(call.create_date)}
                        {call.sales_rep_email ? ` • ${call.sales_rep_email}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-col space-y-2">
                      <button
                        onClick={() => followUpCall(call.id)}
                        disabled={isLoading}
                        className="inline-flex items-center rounded-md bg-yellow-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-yellow-500 disabled:bg-gray-400"
                      >
                        Follow Up
                      </button>
                      {call.status !== 'completed' && (
                        <button
                          onClick={() => addCustomerFromCall(call)}
                          disabled={isLoading}
                          className="inline-flex items-center rounded-md bg-green-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-green-500 disabled:bg-gray-400"
                        >
                          Add Customer
                        </button>
                      )}
                      {call.status === 'interested' && (
                        <button
                          onClick={() => convertToOrder(call)}
                          disabled={isLoading}
                          className="inline-flex items-center rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400"
                        >
                          Create Order
                        </button>
                      )}
                      {call.status !== 'closed-out' && (
                        <button
                          onClick={() => openCloseOutPanel(call.id)}
                          disabled={isLoading}
                          className="inline-flex items-center rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-red-500 disabled:bg-gray-400"
                        >
                          Close Out
                        </button>
                      )}
                    </div>
                  </div>
                  {closeOutCallId === call.id && (
                    <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3">
                      <label className="mb-1 block text-xs font-medium text-red-800">
                        Close-out reason (required)
                      </label>
                      <textarea
                        value={closeOutReason}
                        onChange={(e) => setCloseOutReason(e.target.value)}
                        disabled={isLoading}
                        className="block w-full rounded-md border-red-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm disabled:bg-gray-100"
                        rows={3}
                        placeholder="Enter the final reason for closing out this call"
                      />
                      <div className="mt-2 flex justify-end space-x-2">
                        <button
                          onClick={cancelCloseOut}
                          disabled={isLoading}
                          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:bg-gray-100"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => closeOutCall(call.id)}
                          disabled={isLoading}
                          className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-red-500 disabled:bg-gray-400"
                        >
                          {isLoading ? 'Saving...' : 'Confirm Close Out'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
              })
          )}
        </div>
      </div>
    </div>
  );
}

// Cash vs Trade Widget
function CashVsTradeWidget({ quotaData }) {
  const cashAmount = Number(quotaData?.cash_sales ?? 0);
  const tradeAmount = Number(quotaData?.trade_sales ?? 0);
  const total = cashAmount + tradeAmount;
  const cashPercentage = total > 0 ? (cashAmount / total) * 100 : 0;
  const tradePercentage = total > 0 ? (tradeAmount / total) * 100 : 0;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Cash vs Trade Sales</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-sm font-medium text-gray-700">Cash Sales</span>
            </div>
            <span className="text-sm font-semibold text-gray-900">${cashAmount.toLocaleString()}</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full" 
              style={{ width: `${cashPercentage}%` }}
            ></div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-sm font-medium text-gray-700">Trade Sales</span>
            </div>
            <span className="text-sm font-semibold text-gray-900">${tradeAmount.toLocaleString()}</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full" 
              style={{ width: `${tradePercentage}%` }}
            ></div>
          </div>

          <div className="pt-2 border-t border-gray-200">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Aggregate Sales Activity: <b>${total.toLocaleString()}</b></span>
              <span>{cashPercentage.toFixed(1)}% Cash / {tradePercentage.toFixed(1)}% Trade</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Monthly Quota Widget
function getBusinessDaysRemainingInMonth(referenceDate = new Date()) {
  const current = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
    12,
    0,
    0,
    0
  );
  const monthEnd = new Date(
    current.getFullYear(),
    current.getMonth() + 1,
    0,
    12,
    0,
    0,
    0
  );
  let count = 0;

  for (let day = new Date(current); day <= monthEnd; day.setDate(day.getDate() + 1)) {
    const dayOfWeek = day.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count += 1;
    }
  }

  // The loop is already inclusive of monthEnd when it falls on a weekday.
  // This note keeps the intent explicit and avoids regressions if the logic is refactored.

  return count;
}

function MonthlyQuotaWidget({ quotaData, selectedPeriod }) {
  const today = new Date();
  const isCompletedMonth = selectedPeriod === 'Last Month';
  const quotaGoal = Number(quotaData?.quota_goal ?? quotaData?.quota ?? 25000);
  const achieved = Number(quotaData?.cash_sales ?? quotaData?.achieved ?? 0);
  const tradeSales = Number(quotaData?.trade_sales ?? 0);
  const percentage = Number.isFinite(Number(quotaData?.percent_complete))
    ? Number(quotaData.percent_complete)
    : (quotaGoal > 0 ? (achieved / quotaGoal) * 100 : 0);
  const overUnder = Number(quotaData?.over_under ?? (achieved - quotaGoal));
  const remaining = Math.max(0, quotaGoal - achieved);
  const isAhead = overUnder >= 0;
  const daysLeft = typeof quotaData?.days_left_in_month === 'number'
    ? quotaData.days_left_in_month
    : (isCompletedMonth ? 'Month Completed' : getBusinessDaysRemainingInMonth(today));

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Monthly Sales Quota</h3>
        
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">
              ${achieved.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">
              of ${quotaGoal.toLocaleString()} goal
            </div>
            <div className="text-xs text-gray-500">
              Trade ${tradeSales.toLocaleString()}
            </div>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full ${
                percentage >= 100 ? 'bg-green-500' :
                percentage >= 80 ? 'bg-yellow-500' :
                'bg-red-500'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            ></div>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{percentage.toFixed(1)}% Complete</span>
            <span className={`font-medium ${
              isAhead ? 'text-green-600' : 'text-red-600'
            }`}>
              {isAhead ? `+$${Math.abs(overUnder).toLocaleString()} Over` :
               `$${remaining.toLocaleString()} Remaining`}
            </span>
          </div>

          <div className="text-center text-xs text-gray-500">
            {typeof daysLeft === 'number' ? `${daysLeft} business days left in month` : daysLeft}
          </div>
        </div>
      </div>
    </div>
  );
}

// Top Spots Widget
function TopSpotsWidget({ spots }) {
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status) {
      case 'script needed':
        return 'bg-red-100 text-red-800 ring-red-600/20';
      case 'script approved':
        return 'bg-yellow-100 text-yellow-800 ring-yellow-600/20';
      case 'produced':
        return 'bg-blue-100 text-blue-800 ring-blue-600/20';
      case 'approved':
        return 'bg-purple-100 text-purple-800 ring-purple-600/20';
      case 'live':
        return 'bg-green-100 text-green-800 ring-green-600/20';
      default:
        return 'bg-gray-100 text-gray-800 ring-gray-600/20';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <ArrowUpIcon className="h-4 w-4 text-red-500" />;
      case 'medium':
        return <ArrowUpIcon className="h-4 w-4 text-yellow-500 transform rotate-45" />;
      case 'low':
        return <ArrowDownIcon className="h-4 w-4 text-green-500" />;
      default:
        return null;
    }
  };

  const handleSpotClick = (spot) => {
    // Navigate to spot view/edit page
    navigate(`/spotview/${spot.id}`);
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center">
            <MicrophoneIcon className="h-5 w-5 mr-2 text-indigo-500" />
            Top Priority Spots
          </h3>
          <button
            onClick={() => navigate('/spots')}
            className="text-sm text-indigo-600 hover:text-indigo-500 flex items-center"
          >
            <EyeIcon className="h-4 w-4 mr-1" />
            View All
          </button>
        </div>
        
        <div className="space-y-3">
          {spots.map((spot) => (
            <div 
              key={spot.id || spot.rank} 
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
              onClick={() => handleSpotClick(spot)}
            >
              <div className="flex items-center flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white mr-3 ${
                  spot.rank === 1 ? 'bg-red-500' :
                  spot.rank === 2 ? 'bg-yellow-500' :
                  'bg-orange-500'
                }`}>
                  {spot.rank}
                </div>
                <div className="flex-1">
                  <div className="flex items-center">
                    <div className="text-sm font-medium text-gray-900 mr-2">{spot.cart}</div>
                    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStatusColor(spot.status)}`}>
                      {spot.status}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">{spot.description}</div>
                  <div className="text-xs text-gray-600 mt-1">{spot.spot_title}</div>
                </div>
              </div>
              <div className="flex items-center ml-4">
                {getPriorityIcon(spot.priority)}
              </div>
            </div>
          ))}
        </div>

        {/* Status Legend */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 mb-2">Status Legend:</div>
          <div className="grid grid-cols-2 gap-1 text-xs">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
              <span>Script Needed</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></div>
              <span>Script Approved</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
              <span>Produced</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-purple-500 rounded-full mr-1"></div>
              <span>Approved</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
              <span>Live</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const storeUser = useUserStore((state) => state.user);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topSpots, setTopSpots] = useState(mockTopSpots);
  const [selectedPeriod, setSelectedPeriod] = useState('This Month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const defaultSalesQuota = Number(
    storeUser?.sales_quota ??
    storeUser?.authResponse?.profile?.profileinfo?.preferences?.monthly_sales_target ??
    storeUser?.profile?.sales_quota ??
    25000
  );
  const [quotaData, setQuotaData] = useState({
    quota_goal: defaultSalesQuota,
    cash_sales: 0,
    trade_sales: 0,
    days_left_in_month: getBusinessDaysRemainingInMonth(new Date()),
    percent_complete: 0,
    over_under: -defaultSalesQuota,
  });

  // Initialize default dates
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFromDate(firstDayOfMonth.toISOString().split('T')[0]);
    setToDate(lastDayOfMonth.toISOString().split('T')[0]);
  }, []);

  // Handle period changes
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    // Here you would typically fetch new data based on the selected period
    console.log('Period changed to:', period, 'From:', fromDate, 'To:', toDate);
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
    // Refetch data when custom dates change
    console.log('From date changed to:', date);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
    // Refetch data when custom dates change
    console.log('To date changed to:', date);
  };

  // Fetch dashboard data on mount and when period changes
  useEffect(() => {
    // Replace with actual API calls that use the selected period and dates
    // fetchDashboardStats(selectedPeriod, fromDate, toDate);
  }, [selectedPeriod, fromDate, toDate]);

  useEffect(() => {
    let isMounted = true;

    const fetchTopSpots = async () => {
      try {
        const response = await fetch(apiUrl('/dashboard/spots?limit=5'), {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const spots = Array.isArray(data)
          ? data
          : (Array.isArray(data?.data) ? data.data : []);
        console.log('Dashboard spots returned:', spots);

        const mappedSpots = spots.map((spot, index) => {
          const numericPriority = Number(spot?.priority ?? 0);
          const mappedPriority = numericPriority >= 2 ? 'high' : (numericPriority === 1 ? 'medium' : 'low');

          return {
            id: spot?._id || `${spot?.cart || 'spot'}-${index}`,
            rank: index + 1,
            cart: `${spot?.cart || ''}${spot?.cut ? `-${spot.cut}` : ''}`,
            description: spot?.description || '',
            spot_title: spot?.spot_title || '',
            customer: spot?.customer_name || '',
            status: spot?.status || 'produced',
            priority: mappedPriority
          };
        });

        if (isMounted) {
          setTopSpots(mappedSpots);
        }
      } catch (error) {
        console.error('Error fetching dashboard top spots:', error);
      }
    };

    fetchTopSpots();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchRecentOrders = async () => {
      const salesRepEmail =
        storeUser?.email ||
        storeUser?.authResponse?.profile?.email ||
        storeUser?.authResponse?.profile?.profileinfo?.email;

      if (!salesRepEmail) {
        return;
      }

      try {
        const response = await fetch(apiUrl(`/dashboard/orders/${encodeURIComponent(salesRepEmail)}?limit=5`), {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const orders = Array.isArray(data) ? data : [];

        const mappedOrders = orders.map((order) => {
          const cashAmount = Number(order?.total_cost || 0);
          const tradeAmount = Number(order?.total_trade || 0);
          const totalValue = Number(order?.total_value || cashAmount + tradeAmount);

          return {
            id: order?.id || order?._id || '',
            customer: order?.customer_name || '',
            amount: `$${totalValue.toLocaleString()}`,
            type: tradeAmount > 0 && cashAmount <= 0 ? 'Trade' : (cashAmount > 0 && tradeAmount <= 0 ? 'Cash' : 'Mixed'),
            startDate: order?.start_date || '',
            endDate: order?.end_date || ''
          };
        });

        if (isMounted) {
          setRecentOrders(mappedOrders);
        }
      } catch (error) {
        console.error('Error fetching recent dashboard orders:', error);
      }
    };

    fetchRecentOrders();

    return () => {
      isMounted = false;
    };
  }, [storeUser]);

  useEffect(() => {
    let isMounted = true;

    const fetchQuota = async () => {
      try {
        const baseDate = fromDate ? new Date(`${fromDate}T00:00:00`) : new Date();
        const salesRepEmail =
          storeUser?.email ||
          storeUser?.authResponse?.profile?.email ||
          storeUser?.authResponse?.profile?.profileinfo?.email;

        const quotaRequest = {
          month: baseDate.getMonth() + 1,
          year: baseDate.getFullYear(),
          ...(salesRepEmail ? { sales_rep_email: salesRepEmail } : {})
        };

        const response = await fetch(apiUrl('/dashboard/quota'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(quotaRequest)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (isMounted) {
          console.log('Fetched dashboard quota:', data);
          setQuotaData(data);
        }
      } catch (error) {
        console.error('Error fetching dashboard quota:', error);
      }
    };

    fetchQuota();

    return () => {
      isMounted = false;
    };
  }, [fromDate, storeUser]);

  // Now Playing: fetch from /tracking/latest once per minute
  const [nowPlaying, setNowPlaying] = useState(null);

  useEffect(() => {
    let mounted = true;
    let intervalId = null;

    const fetchNowPlaying = async () => {
      try {
        const res = await fetch(apiUrl('/tracking/latest'));
        if (!res.ok) return;
        const data = await res.json();
        const artist = data?.artist || data?.Artist || '';
        const title = data?.title || data?.Title || '';

        if (!mounted) return;

        // Only update when values change
        setNowPlaying((prev) => {
          if (!prev) return (artist || title) ? { artist, title } : null;
          if (prev.artist !== artist || prev.title !== title) {
            return (artist || title) ? { artist, title } : null;
          }
          return prev;
        });
      } catch (err) {
        // ignore network errors for now
        console.debug('NowPlaying fetch error', err);
      }
    };

    // initial fetch then every 60s
    fetchNowPlaying();
    intervalId = setInterval(fetchNowPlaying, 60 * 1000);

    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
      <Titlebar title="Dashboard" nowPlaying={nowPlaying} />
      
      <div className="z-1 mx-auto max-w-7xl px-2 py-4 sm:px-2 lg:px-2">
        {/* Time Period Selector */}
        <div className="flex items-center justify-between mb-6">
          <TimePeriodSelector 
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={handleFromDateChange}
            onToDateChange={handleToDateChange}
          />
        </div>

        {/* Top Row: Monthly Quota + Cash vs Trade */}
        <div className="grid gap-6 lg:grid-cols-2">
          <MonthlyQuotaWidget quotaData={quotaData} selectedPeriod={selectedPeriod} />
          <CashVsTradeWidget quotaData={quotaData} />
        </div>

        {/* Second Row: Recent Orders (full width) */}
        <div className="mt-6">
          <RecentOrdersTable orders={recentOrders} />
        </div>

        {/* Third Row: Sales Call Tracker + Top Priority Spots */}
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <SalesCallTracker />
          <TopSpotsWidget spots={topSpots} />
        </div>
      </div>
    </div>      
  );
}

export default Dashboard;